import React from 'react';
import { Bot, BookOpen, Settings, MessageSquare, Smartphone, Zap } from 'lucide-react';
import useScrollFadeIn from '../hooks/useScrollFadeIn';

const steps = [
  {
    icon: Bot,
    title: 'Create Your Agent',
    description: 'Give your AI assistant a name and personality that matches your brand.',
  },
  {
    icon: BookOpen,
    title: 'Teach It Your Business',
    description: 'Upload documents, FAQs, and service info so it knows everything about you.',
  },
  {
    icon: Settings,
    title: 'Choose Features',
    description: 'Enable lead capture, appointment booking, email sending, and more.',
  },
  {
    icon: MessageSquare,
    title: 'Test Responses',
    description: 'Chat with your assistant locally to make sure it responds accurately.',
  },
  {
    icon: Smartphone,
    title: 'Connect WhatsApp',
    description: 'Link your WhatsApp Business number in just a few clicks.',
  },
  {
    icon: Zap,
    title: 'Start Replying Automatically',
    description: 'Your AI assistant goes live and handles customer conversations 24/7.',
  },
];

export default function HowItWorks() {
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <section
      id="how-it-works"
      className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 relative"
      style={{ background: 'var(--bg-section-1)' }}
    >
      <div
        ref={ref}
        className={`max-w-6xl mx-auto transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Section heading */}
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-heading)' }}>
            How It Works
          </h2>
          <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-body)' }}>
            Get your AI assistant up and running in six simple steps.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  boxShadow: 'var(--shadow-card)',
                  backdropFilter: 'blur(var(--glass-blur))',
                  WebkitBackdropFilter: 'blur(var(--glass-blur))',
                  transitionDelay: `${index * 80}ms`,
                }}
              >
                {/* Step number badge */}
                <div
                  className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center mb-4"
                  style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
                >
                  {index + 1}
                </div>

                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'var(--bg-icon)' }}
                >
                  <Icon className="w-6 h-6" style={{ color: 'var(--text-accent)' }} />
                </div>

                {/* Text */}
                <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-heading)' }}>
                  {step.title}
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-body)' }}>
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
